import { h } from 'preact'
import { i18n } from 'i18n/i18n'

import currencyHelper from 'utils/currency-helper'

import { SelectSingleOption } from 'common-modules/selects/calculator-select-option'
import { CalculatorSelectSearch } from 'common-modules/selects/calculator-select-search'
import { useSelectVisibility } from 'hooks/useSelectVisibility'

interface CalculatorCurrencySelectProps {
  isBaseCurrencyComponent: boolean
  baseCurrency: string
  counterCurrency: string
  handleSetBaseCurrency: (value: string) => void
  handleSetCounterCurrency: (value: string) => void
  fixedCurrencyList: string[]
  isDarkMode?: boolean
  isSearchable?: boolean
  getOptionSubLabel?: (option: string) => string
  disableSwapOnCollision?: boolean
}

const getCurrencySearchText = (option: string): string =>
  `${option} ${i18n(`currencies.${option}`)} ${currencyHelper.getCurrencyCountry(option)}`

export const CalculatorCurrencySelect = ({
  isBaseCurrencyComponent,
  baseCurrency,
  counterCurrency,
  handleSetBaseCurrency,
  handleSetCounterCurrency,
  fixedCurrencyList,
  isDarkMode,
  isSearchable,
  getOptionSubLabel,
  disableSwapOnCollision,
}: CalculatorCurrencySelectProps) => {
  const {
    isSelectVisible,
    toggleSelectVisibility,
    triggerRef,
    containerRef,
    listRef,
    handleTriggerKeyDown,
    handleListKeyDown,
    searchValue,
    setSearchValue,
    searchInputRef,
  } = useSelectVisibility({ enableSearch: isSearchable })

  const selectedCurrency = isBaseCurrencyComponent ? baseCurrency : counterCurrency
  const componentName = isBaseCurrencyComponent ? 'base' : 'counter'

  const visibleCurrencyList = isSearchable
    ? fixedCurrencyList.filter((option) =>
        getCurrencySearchText(option).toLowerCase().includes(searchValue.toLowerCase().trim()),
      )
    : fixedCurrencyList

  const setSelectedCurrency = (selectedOption: string): void => {
    if (isBaseCurrencyComponent) {
      handleSetBaseCurrency(selectedOption)
      if (!disableSwapOnCollision && selectedOption === counterCurrency) {
        handleSetCounterCurrency(baseCurrency)
      }
    } else {
      handleSetCounterCurrency(selectedOption)
      if (!disableSwapOnCollision && selectedOption === baseCurrency) {
        handleSetBaseCurrency(counterCurrency)
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className="preact-select preact-select--currency"
      data-currency-holder={componentName}
    >
      <div
        ref={triggerRef}
        className="preact-select__trigger"
        aria-expanded={isSelectVisible}
        role="button"
        tabIndex={0}
        onClick={toggleSelectVisibility}
        onKeyDown={handleTriggerKeyDown}
        data-calculator-currency={componentName}
        data-value={selectedCurrency}
      >
        <span
          className={`preact-select__option-flag icon-currency-round-${selectedCurrency} icon-currency-round-${selectedCurrency}-dims`}
        />
        <span className="preact-select__option-name preact-select__option-name--with-flag">{selectedCurrency}</span>
        <span className={`preact-select__chevron ${isDarkMode ? 'is-dark-mode' : ''}`} />
      </div>
      {isSelectVisible && (
        <div
          ref={listRef}
          className="preact-select__options-list"
          role="listbox"
          onKeyDown={handleListKeyDown}
          onClick={toggleSelectVisibility}
        >
          {isSearchable && (
            <CalculatorSelectSearch
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              searchInputRef={searchInputRef}
              hasResults={visibleCurrencyList.length > 0}
            />
          )}
          {visibleCurrencyList.map((option) => (
            <SelectSingleOption
              key={option}
              option={option}
              optionName={option}
              handleSetClickedOption={setSelectedCurrency}
              isWithFlags={true}
              subLabel={getOptionSubLabel?.(option)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
